<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TopBar from './components/TopBar.vue';
import AppHeader from './components/AppHeader.vue';
import OrbitHead from './components/OrbitHead.vue';
import SignInPanel from './components/SignInPanel.vue';
import SetPasswordPanel from './components/SetPasswordPanel.vue';
import SquadPager from './components/SquadPager.vue';
import PlayerPanel from './components/PlayerPanel.vue';
import ConfirmDialog from './components/ConfirmDialog.vue';
import AppSnackbar from './components/AppSnackbar.vue';
import NewPlayerDialog from './components/NewPlayerDialog.vue';
import { SQUAD_PAGES } from './domain/squadPages.js';
import { useAuth } from './composables/useAuth.js';
import { usePlayers } from './composables/usePlayers.js';
import { useFootballClubs } from './composables/useFootballClubs.js';
import { useUnsavedChanges } from './composables/useUnsavedChanges.js';
import { useLanguageSync } from './composables/usePushPreferences.js';
import { isConfigured } from './supabase/client.js';

const { locale } = useI18n();
const { init, isSignedIn, can, role, loading: authLoading, hasInviteLink } = useAuth();
const { loadOnce, reset, syncSource, refreshForReader, syncContracts } = usePlayers();
const clubRegister = useFootballClubs();
const { hasUnsaved } = useUnsavedChanges();

/* Sprache des Nutzers für den Push-Text mitführen (Anmeldung, Sprachwechsel). */
useLanguageSync();

/* Browser-native Warnung beim Verlassen/Neuladen der Seite, solange
   irgendwo ungespeicherte Änderungen offen stehen (Spieler-Panel,
   Rollenänderung in der Nutzerverwaltung — siehe useUnsavedChanges.js).
   Der Text ist vom Browser vorgegeben, nicht gestaltbar. */
function onBeforeUnload(e) {
  if (!hasUnsaved.value) return;
  e.preventDefault();
  e.returnValue = '';
}

/* Die Swipe-Seiten (Kopf + Liste) erscheinen erst, wenn die App wirklich zu sehen ist. Davor — nicht
   verbunden, Laden, Passwort setzen, Anmeldung — steht unter der Wortmarke nur der Kopf der Kaderseite
   (Wappen, Name, Tag-Zeile), ohne Umlaufbahn, Pfeile und Punkte. */
const KADER_ONLY = [SQUAD_PAGES[0]];
const showApp = computed(
  () => isConfigured && !authLoading.value && isSignedIn.value && !(hasInviteLink.value && !passwordSetupDone.value)
);

/* Anlege-Dialog fuer neue Spieler. */
const newPlayerOpen = ref(false);

/* hasInviteLink bleibt fuer die Lebensdauer der Seite wahr (haengt an
   INITIAL_HASH, siehe useAuth.js) -- dieses Flag schaltet nach erfolgreich
   gesetztem Passwort zurueck in die normale Ansicht, ohne dass dafuer ein
   Neuladen noetig waere. */
const passwordSetupDone = ref(false);

/* Gäste: Liste still auffrischen, wenn die App wieder sichtbar wird (siehe usePlayers.js
   refreshForReader — für andere Rollen tut das nichts). */
function onVisible() {
  if (document.visibilityState === 'visible') refreshForReader();
}

onMounted(async () => {
  document.documentElement.lang = locale.value;
  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);
  await init();
});
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', onBeforeUnload);
  document.removeEventListener('visibilitychange', onVisible);
  window.removeEventListener('focus', onVisible);
});

/* Seitensprache mitführen — wichtig für Vorleseprogramme und Silbentrennung. */
watch(locale, (l) => {
  document.documentElement.lang = l;
});

/* Daten erst nach der Anmeldung holen; ohne Sitzung liefern die
   Datenbankregeln ohnehin nichts. */
watch(
  isSignedIn,
  (signedIn) => {
    if (signedIn) {
      loadOnce();
      /* Klubregister einmal pro Sitzung; schlägt es fehl, zeigt die App den alten Vereinstext */
      clubRegister.ensureLoaded();
    } else {
      reset();
      clubRegister.reset();
    }
  },
  { immediate: true }
);

/* Ändert sich die Rolle bei offener App so, dass der Lesepfad wechselt (Gast <-> Mitarbeiter),
   wird die Liste neu aus der richtigen Quelle geladen. */
watch(role, () => syncSource());

/* Vertragsdaten nur für Rollen mit Recht (admin, board, data) holen. Die Rolle
   steht erst fest, wenn das Profil geladen ist — deshalb hier beobachten statt
   einmalig beim Laden der Spieler. Verliert man das Recht, verschwinden die
   Daten aus dem Speicher. */
watch(
  () => can('contracts', 'read'),
  () => syncContracts(),
  { immediate: true }
);
</script>

<template>
  <v-app>
    <v-main class="main">
      <TopBar />
      <AppHeader />
      <OrbitHead v-if="!showApp" :pages="KADER_ONLY" :pos="0" :active-index="0" />

      <div v-if="!isConfigured" class="notice error">
        {{ $t('errors.notConnected') }}
      </div>

      <div v-else-if="authLoading" class="notice">
        {{ $t('common.loading') }}
      </div>

      <SetPasswordPanel
        v-else-if="hasInviteLink && isSignedIn && !passwordSetupDone"
        @done="passwordSetupDone = true"
      />

      <SignInPanel v-else-if="!isSignedIn" />

      <template v-else>
        <SquadPager @add="newPlayerOpen = true" />
        <PlayerPanel />
        <NewPlayerDialog v-model="newPlayerOpen" />
      </template>

      <!-- Rückfrage vor unwiderruflichen Aktionen. Hängt einmal hier und
           zeigt sich nur, wenn confirm() aufgerufen wurde. -->
      <ConfirmDialog />

      <!-- Kurze Rückmeldung nach einer Aktion ("Player deleted"), siehe useSnackbar.js -->
      <AppSnackbar />
    </v-main>
  </v-app>
</template>

<style scoped>
/* Vuetify reserviert in v-main Platz für Leisten, die es hier nicht gibt. */
.main {
  --v-layout-top: 0px;
  padding: 0 !important;
}
.notice {
  padding: 32px 0;
  text-align: center;
  color: var(--muted);
  font-size: 0.85rem;
}
.notice.error {
  color: var(--amber);
}
</style>
